import { Common } from "./types";

export * from "@common/components/FormSurvey/types";
export * as Kb from "@common/modules/kb/types";
export * as System from "@common/modules/system/types";
export * from "@common/modules/user/types";

export type AppTheme = "Day" | "Night";

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export namespace Api {
  export type CreatedUpdated = {
    createdByUserId: number;
    createdOnDate: string;
    lastModifiedByUserId: number | null;
    lastModifiedOnDate: string | null;
  };

  export interface DropdownItem<T = Primitive> {
    caption: string;
    group?: string;
    value: T;

    sortOrder?: number;

    parentValue?: T | null;
  }

  export interface LoadPageArgs<T> {
    page: number;
    size: number;
    search?: string;
    sortBy?: Extract<keyof T, string>;
    order?: "asc" | "desc";
    filters?: GenericMap<string | number | boolean | null>;
  }

  export interface PageRequest {
    page: number;
    size: number;
    searchText?: string;
    sortBy?: string;
    order?: SortOrder;
    pageDate?: string;
  }

  export type LocalEntityRequest = Omit<PageRequest, "searchText" | "order"> & {
    cultureCode?: string;
    isLocalized?: boolean;
    search?: string;
  };

  export type LocalMediaEntity = {
    cultureCode: string;

    mediaId: string;
    mediaSourceKey: Media.Source | null;
    mediaSourceId: string;
    mediaTypeKey: Media.Type;
    mediaName: string;

    localMediaId: string | null;
    localMediaSourceKey: Media.Source | null;
    localMediaSourceId: string | null;
    localMediaTypeKey: Media.Type | null;
    localMediaName: string | null;

    isLocalized: boolean;
  };

  export namespace V1 {
    export type DropdownItem<T = Primitive> = Api.DropdownItem<T> & {
      label: string | null;
      groupCaption?: string | null;
      groupLabel?: string | null;
      sortOrder?: number;
    };
  }

  export namespace Options {
    export type PaymentMethodOption = OptionItem<string> & {
      appKey: MarketplaceAppKey | null;
      doesSupportManualCapture: boolean;
    };

    export type UserPaymentMethodOption = OptionItem<string> & {
      paymentMethodHint: string;
      doesSupportManualCapture: boolean;
    };

    export type SubdivisionOption = OptionItem<string> & {
      countryTaxNumber: string | null;
      subdivisionTaxNumber: string | null;
    };
  }
}

export interface Address {
  city: string;
  company: string;
  companyTaxNumber: string;
  email: string;
  firstName: string;
  countryCode: string;
  subdivisionCode: string;
  lastName: string;
  telephone: string;
  postalCode: string;
  street: string;
  unit: string;

  latitude?: number;
  longitude?: number;
}

export type BillingAddress = {
  billingFirstName: string;
  billingLastName: string;
  billingCompany: string;
  billingCompanyTaxNumber: string;
  billingCountryCode: string;
  billingSubdivisionCode: string;
  billingCity: string;
  billingPostalCode: string;
  billingStreet: string;
  billingUnit: string;
  billingEmail: string;
  billingTelephone: string;

  billingLatitude?: number;
  billingLongitude?: number;
};

export type ShippingAddress = {
  shippingFirstName: string;
  shippingLastName: string;
  shippingCompany: string;
  shippingCompanyTaxNumber: string;
  shippingCountryCode: string;
  shippingSubdivisionCode: string;
  shippingCity: string;
  shippingPostalCode: string;
  shippingStreet: string;
  shippingUnit: string;
  shippingEmail: string;
  shippingTelephone: string;
  shippingPickupPointCode: string;
  shippingLatitude?: number | null;
  shippingLongitude?: number | null;
};

export type AppInfoAi = {
  agentId: string | null;
  /** IMPORTANT! This is the default value. Use AppContext.ai.balance instead */
  creditBalance: number;
  isTextGenerationEnabled: boolean;
};
export type AppInfoIntegration = {
  hostUrl: string;
  mediaServiceUrl: string;
};
export type AppInfoSystem = {
  brandDomain: string;
  brandKey: BrandKey;
  company: string | null;
  createdOnDate: string;
  defaultSendingEmail: string;
  id: string;
  isDemo: boolean;
  isHost: boolean;
  liveRootDomain: string;
  prefix: string;
  statusKey: TenantStatus;
  upgradedOnDate: string | null;
  version: string;
};
export type AppInfoBunny = {
  hostName: string;
  libraryId: number;
};
export type AppInfoBusiness = {
  timeZone: string;
  timeZoneIana: string;
  unitStandardKey: UnitStandardKey;
};
export type AppInfoHost = {
  bunnyLibraryId: string;
  publicUrl: string;
  url: string;
};
export interface AppInfo {
  ai: AppInfoAi;
  awsBaseUrl: string;
  billingCurrencyCode: string;
  bunny: AppInfoBunny | null;
  business: AppInfoBusiness;
  host: AppInfoHost;
  installedApps: MarketplaceAppKey[];
  integration: AppInfoIntegration;
  currencyCode: string;
  cultureCode: string;
  countryCode: string;
  primaryCultureCode: string;
  isMultiCurrency: boolean;
  isWalletToWalletTransfersEnabled: boolean;
  system: AppInfoSystem;
  user: AppConfigUser | null;
}

export type MarketplaceAppKey =
  | "BunnyVideo"
  | "Forms"
  | "GoogleMapsGeocode"
  | "NetworkMarketing"
  | "Numverify"
  | "TrafficTracker"
  | "Wallets"
  | "WhatsApp";

export interface AppConfigUser {
  affiliateDate: string | null;
  company: string | null;
  displayName: string;
  email: string;
  telephone: string | null;
  id: number;
  isStaff: boolean;
  isSuperUser: boolean;
  permissionKeys: string[] | null;
  profileMediaId: string | null;
  profilePictureMediaSourceId: Common.Media.Source | null;
  supplierCount: number;
  userFirstName: string;
  userLastName: string;
  warehouseCount: number;
}

export type DayOfWeek = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

export type GenericMap<T> = {
  [key: string]: T;
};

export type TypedMap<K extends string, T> = {
  [key in K]: T;
};

export enum IconType {
  Add = "far fa-plus",
  Adjust = "far fa-adjust",
  Align = "far fa-align",
  AlignCenter = "far fa-align-center",
  AlignLeft = "far fa-align-left",
  AlignJustify = "far fa-align-justify",
  AlignRight = "far fa-align-right",
  AngleDown = "far fa-angle-down",
  AngleLeft = "far fa-angle-left",
  AngleRight = "far fa-angle-right",
  AngleUp = "far fa-angle-up",
  Archive = "far fa-archive",
  ArrowDown = "far fa-arrow-down",
  ArrowDownLeft = "far fa-arrow-down-left",
  ArrowDownLeftAndArrowUpRightToCenter = "far fa-arrow-down-left-and-arrow-up-right-to-center",
  ArrowDownRight = "far fa-arrow-down-right",
  ArrowLeft = "far fa-arrow-left",
  ArrowRight = "far fa-arrow-right",
  Arrows = "far fa-arrows",
  ArrowsFromLine = "far fa-arrows-from-line",
  ArrowsLeftRight = "far fa-arrows-left-right",
  ArrowsToLine = "far fa-arrows-to-line",
  ArrowTurnDownLeft = "far fa-arrow-turn-down-left",
  ArrowUp = "far fa-arrow-up",
  ArrowUpLeft = "far fa-arrow-up-left",
  Back = "far fa-arrow-circle-left",
  Ban = "far fa-ban",
  BarsFilter = "far fa-bars-filter",
  BarsStaggered = "far fa-bars-staggered",
  Basket = "far fa-basket-shopping",
  Bell = "far fa-bell",
  BlockQuote = "far fa-block-quote",
  Blog = "far fa-blog",
  Bold = "far fa-bold",
  Bolt = "far fa-bolt",
  Book = "far fa-book",
  Brackets = "far fa-brackets-curly",
  Broom = "far fa-broom",
  Brush = "far fa-brush",
  BuildingColumns = "far fa-building-columns",
  Calendar = "far fa-calendar-alt",
  CalendarClock = "far fa-calendar-clock",
  CalendarO = "far fa-calendar-o",
  CalendarWeek = "far fa-calendar-week",
  CaretDown = "far fa-caret-down",
  CaretUp = "far fa-caret-up",
  ChartBar = "far fa-chart-bar",
  ChartMixed = "far fa-chart-mixed",
  ChartNetwork = "far fa-chart-network",
  ChartPyramid = "far fa-chart-pyramid",
  ChartPyramidVertical = "far fa-chart-pyramid fa-flip-vertical",
  Check = "far fa-check",
  CheckCircle = "far fa-check-circle",
  ChevronDown = "far fa-chevron-down",
  ChevronLeft = "far fa-chevron-left",
  ChevronRight = "far fa-chevron-right",
  ChevronUp = "far fa-chevron-up",
  Circle = "far fa-circle",
  CircleCheckSolid = "fas fa-circle-check",
  CircleNodes = "far fa-circle-nodes",
  CircleNotch = "far fa-circle-o-notch fa-spin",
  ClipboardCheck = "far fa-clipboard-list-check",
  ClipboardUser = "far fa-clipboard-user",
  Clock = "far fa-clock",
  Clone = "far fa-clone",
  Cloud = "far fa-cloud",
  CloudDownload = "far fa-cloud-download",
  Code = "far fa-code",
  CodeBranch = "far fa-code-branch",
  Cog = "far fa-cog",
  Comment = "far fa-comment",
  CommentQuestionSolid = "fas fa-comment-question",
  Comments = "far fa-comments-alt",
  Copy = "far fa-copy",
  CreditCard = "far fa-credit-card",
  CreditCardFront = "far fa-credit-card-front",
  Cross = "far fa-times",
  CrossCircle = "far fa-times-circle",
  CrossCircleSolid = "far fa-times-circle",
  Crown = "far fa-crown",
  Delete = "far fa-trash",
  Desktop = "far fa-desktop",
  Disabled = "far fa-times-circle",
  DollarSign = "far fa-dollar-sign",
  DoubleRight = "far fa-angle-double-right",
  Download = "far fa-download",
  EarthAmericas = "far fa-earth-americas",
  EditPencil = "far fa-edit",
  EllipsisHorizontal = "far fa-ellipsis-h",
  EllipsisVertical = "far fa-ellipsis-v",
  EmptyClock = "far fa-clock",
  Envelope = "far fa-envelope",
  Envelopes = "far fa-envelopes",
  Exchange = "far fa-exchange",
  Execute = "far fa-play",
  ExitFullscreen = "far fa-compress",
  ExternalLink = "far fa-external-link",
  ExtremeDanger = "far fa-radiation",
  Eye = "far fa-eye",
  EyeSlash = "far fa-eye-slash",
  False = "far fa-times",
  File = "far fa-file-alt",
  FileDashedLine = "far fa-file-dashed-line",
  FileDownload = "far fa-file-download",
  FileDownloadLight = "fal fa-file-download",
  FileLight = "fal fa-file-alt",
  FileSolid = "fas fa-file",
  FileUpload = "far fa-file-upload",
  FileVideo = "far fa-file-video",
  Filter = "far fa-filter",
  FlagCheckered = "far fa-flag-checkered",
  FlagSwallowtail = "far fa-flag-swallowtail",
  FlagSwallowtailSolid = "fas fa-flag-swallowtail",
  Folder = "far fa-folder",
  FolderOpen = "far fa-folder-open",
  Font = "far fa-font",
  Friends = "far fa-user-friends",
  FullCircle = "far fa-circle",
  Fullscreen = "far fa-expand",
  Funnel = "far fa-triangle fa-flip-vertical",
  Gift = "far fa-gift",
  Globe = "far fa-globe",
  GraduationCap = "far fa-graduation-cap",
  Grid2 = "far fa-grid-2",
  GripHorizontal = "far fa-grip-horizontal",
  GripVertical = "far fa-grip-vertical",
  GripVerticalSolid = "fas fa-grip-vertical",
  GripVerticalDots = "fas fa-grip-dots-vertical",
  H1 = "far fa-h1",
  H2 = "far fa-h2",
  H3 = "far fa-h3",
  H4 = "far fa-h4",
  H5 = "far fa-h5",
  H6 = "far fa-h6",
  Hand = "far fa-hand-rock",
  HandRock = "far fa-hand-rock",
  HandStop = "far fa-hand",
  Hashtag = "far fa-hashtag",
  HelmetBattle = "far fa-helmet-battle",
  History = "far fa-history",
  Home = "far fa-home",
  Image = "far fa-image",
  Infinity = "far fa-infinity",
  Info = "far fa-info-circle",
  Italic = "far fa-italic",
  Loader = "fa fa-loader fa-spin",
  LayerGroup = "far fa-layer-group",
  LienChart = "far fa-line-chart",
  LifeRing = "far fa-life-ring",
  Link = "far fa-link-simple",
  List = "far fa-list",
  ListDropdown = "far fa-list-dropdown",
  ListRadio = "far fa-list-radio",
  Lock = "far fa-lock",
  LongArrowLeft = "far fa-long-arrow-left",
  LongArrowRight = "far fa-long-arrow-right",
  Manage = "far fa-wrench",
  MapMarker = "far fa-map-marker-alt",
  MapSigns = "far fa-map-signs",
  Maximize = "far fa-window-maximize",
  Medal = "far fa-medal",
  Memo = "far fa-memo",
  Message = "far fa-envelope",
  MessagePlus = "far fa-message-plus",
  Mic = "far fa-microphone",
  Minus = "far fa-minus",
  Mobile = "far fa-mobile",
  Moon = "far fa-moon",
  OrderComment = "far fa-message-dots",
  OrderedList = "far fa-list-ol",
  P = "far fa-p",
  PaintBrush = "far fa-paint-brush",
  Palette = "far fa-palette",
  PaperPlane = "far fa-paper-plane",
  PaperPlaneTop = "far fa-paper-plane-top",
  Pause = "far fa-pause-circle",
  Page = "far fa-page",
  Pay = "far fa-credit-card",
  Pencil = "far fa-pencil",
  Pen = "far fa-pen",
  PenField = "far fa-pen-field",
  Pending = "far fa-clock",
  Phone = "far fa-phone-alt",
  Play = "far fa-play",
  PlayCircle = "far fa-play-circle",
  Plus = "far fa-plus",
  Program = "far fa-university",
  Puzzle = "far fa-puzzle-piece",
  Question = "far fa-question",
  QuestionCircle = "far fa-question-circle",
  QuestionCircleSolid = "fas fa-question-circle",
  Recycle = "far fa-recycle",
  Redo = "far fa-redo",
  Refresh = "far fa-sync",
  Reply = "far fa-reply",
  RightFromBracket = "far fa-right-from-bracket",
  Rocket = "far fa-rocket",
  Run = "far fa-play",
  SaveDisk = "far fa-save",
  SaveSolid = "fas fa-check-circle",
  Search = "far fa-search",
  Settings = "far fa-gear",
  Share = "far fa-share-from-square",
  Sharing = "far fa-wifi",
  Shield = "far fa-shield",
  ShieldAlt = "far fa-shield-alt",
  ShoppingCart = "far fa-shopping-cart",
  Sidebar = "far fa-sidebar",
  SidebarFlip = "far fa-sidebar-flip",
  Signature = "far fa-signature",
  SirenOn = "fas fa-siren-on",
  Sitemap = "far fa-sitemap",
  Sliders = "far fa-sliders",
  Sparkles = "fa-regular fa-sparkles",
  Square = "fas fa-square",
  SquareBrackets = "far fa-brackets-square",
  Star = "far fa-star",
  StarHalf = "far fa-star-half-alt",
  StarSolid = "fas fa-star",
  StickyNote = "far fa-sticky-note",
  Stopwatch = "far fa-stopwatch",
  Strikethrough = "far fa-strikethrough",
  SunBright = "far fa-sun-bright",
  SuqareCheck = "far fa-square-check",
  Sync = "far fa-far fa-sync",
  SyncAlt = "far fa-sync-alt",
  Table = "far fa-table-cells",
  TableLayout = "far fa-table-layout",
  Tablet = "far fa-tablet",
  Tag = "far fa-tag",
  Text = "far fa-text",
  TextSize = "far fa-text-size",
  ThumbsDown = "far fa-thumbs-down",
  ThumbsDownInverse = "far fa-thumbs-down fa-inverse",
  ThumbsUp = "far fa-thumbs-up",
  ThumbsUpInverse = "far fa-thumbs-up fa-inverse",
  Thumbtack = "far fa-thumbtack",
  Tick = "far fa-check",
  TickCircle = "far fa-check-circle",
  TicketComment = "far fa-comment-dots",
  ToggleOff = "far fa-toggle-off",
  ToggleOn = "far fa-toggle-on",
  Trash = "far fa-trash",
  TrashCheck = "fas fa-trash-check",
  TrashRestore = "far fa-trash-restore",
  TriangleExclamation = "far fa-triangle-exclamation",
  Trophy = "far fa-trophy",
  Truck = "far fa-truck",
  True = "far fa-check",
  Tv = "far fa-tv",
  Underline = "far fa-underline",
  Undo = "far fa-undo",
  Unlock = "far fa-unlock",
  UnlockAlt = "far fa-unlock-alt",
  UnorderedList = "far fa-list-ul",
  Upload = "far fa-upload",
  Usd = "far fa-usd",
  User = "far fa-user",
  UserPlus = "far fa-user-plus",
  Users = "far fa-users",
  Vials = "far fa-vials",
  Video = "far fa-video",
  VideoCamera = "fa fa-video-camera",
  VideoLight = "fal fa-video",
  VolumeUp = "far fa-volume-up",
  Wallet = "far fa-wallet",
  WandMagicSparkles = "far fa-wand-magic-sparkles",
  Warning = "far fa-exclamation-triangle",
  WarningCircle = "far fa-exclamation-circle",
  Youtube = "fab fa-youtube"
}

export type InputTypeProps<TValue, TChange = TValue> = WithValue<TValue> &
  WithOnChange<TChange> & {
    className?: string;
    isDisabled?: boolean;
    name?: string;
    qa?: string;
    style?: React.CSSProperties;
  };

export interface LabelValue<T> {
  label: string;
  value: T;
}

export interface LeftTop {
  left: number;
  top: number;
}

export interface Rect {
  left: number;
  top: number;
  bottom: number;
  right: number;
  height: number;
  width: number;
}

export type OptionItem<T> = LabelValue<T> & {
  children?: OptionItem<T>[];
  descriptions?: OptionItemDescription[];
  disabledTooltip?: string;
  group?: string;
  groupPrepend?: any;
  image?: any;
  isDisabled?: boolean;
  isFaded?: boolean;
  isSeparated?: boolean;
  parentValue?: T | null;
  prepend?: any;
  sortOrder?: number;
};

export type PortalOptionItem = OptionItem<string> & {
  defaultDomain: string;
  domain: string | null;
  subdomain: string | null;
  programId: string | null;
  eventId: string | null;
  isDefault: boolean;
};

export type Point = {
  x: number;
  y: number;
};

export type OptionItemSorted<T> = OptionItem<T> & {
  sortOrder: number;
};

export type OptionItemDescription = { label: string; isHint: boolean };

export type Primitive = string | number | boolean;

export interface SortEnd {
  oldIndex: number;
  newIndex: number;
}

export interface SortInfo<T> {
  key: keyof T;
  order?: SortOrder;
  type?: SortType;
}

export type SortOrder = "asc" | "desc";

export type SortType = "date" | "default" | "natural";

export interface SortEvent<T> {
  item: T;
  prevItem: T | null;
  nextItem: T | null;

  order: SortOrder;
}

export enum SystemColor {
  Blue = "blue",
  Green = "green",
  Red = "red",
  Gray = "gray",
  Orange = "orange",
  Purple = "purple",
  White = "white"
}

export type TenantStatus = "Active" | "Canceled" | "Deleted" | "Deploying" | "Graced" | "Suspended";
export type TenantType = "Demo" | "Prod";
export type BrandKey = "Expomarket" | "Funnelr" | "Netready";

export type TimeUnit = "Second" | "Minute" | "Hour" | "Day" | "Quarter" | "Week" | "Month" | "Year";

export interface WithChildren<T = any> {
  children: T;
}

export interface WithOnChange<T> {
  onChange: (value: T) => void;
}

export interface WithOnChangeDelay {
  onChangeDelay?: number;
}

export interface WithBlurFocus {
  onBlur: () => void;
  onFocus: () => void;
}

export interface WithOptions<TOption> {
  options: TOption[];
}

export interface WithOptionsFn<TOption> {
  options: TOption[] | (() => TOption[]);
}

export interface WithRef<T = any> {
  ref: React.Ref<T | null>;
}

export interface WithValue<T> {
  value: T;
}

export interface WithId<T = string> {
  id: T;
}

export interface WithClassName {
  className: string;
}

export namespace Diagrams {
  export type ItemBase<T> = {
    id: string;
    parents?: string[];
    templateName: string;
    data?: T;
  };

  export type MultiParentItem<T> = ItemBase<T> & {
    parents?: string[];
  };

  export type SingleParentItem<T> = ItemBase<T> & {
    parent?: string;
  };

  export type NodeComponentProps<T> = {
    data: T;
  };

  export type Template<T> = {
    name: string;
    Node: React.ComponentType<NodeComponentProps<T>>;
    height: number;
    width?: number;
  };
}

export namespace Media {
  export type Source = "File" | "SoundCloud" | "URL" | "AwsS3" | "Bunny";
  export type Status = "Deleted" | "Processing" | "Ready" | "Reserved";
  export type Type = "Audio" | "Image" | "Video";
  export type AudioType = "SoundCloud" | "URL";
  export type VideoType = "File" | "URL";

  export interface CloudflareVideoUpload {
    media: Media.Item;
    uploadURL: string;
  }

  export interface BunnyVideoInfo {
    averageWatchTime: number;
    category: string;
    collectionId: string;
    dateUploaded: string;
    encodeProgress: number;
    expiryDateTime: number;
    framerate: number;
    guid: string;
    hasMP4Fallback: boolean;
    height: number;
    isPublic: boolean;
    length: number;
    signature: string;
    status: number;
    storageSize: number;
    thumbnailCount: number;
    thumbnailFileName: string;
    title: string;
    totalWatchTime: number;
    videoLibraryId: number;
    views: number;
    width: number;
  }

  export interface BunnyVideoUploadResponse {
    media: Media.Item;
    videoInfo: BunnyVideoInfo;
  }

  export type Item = Api.Item;
  export type Entity = Api.Entity;
  export namespace Api {
    export interface CreateRequest {
      sourceId: string;
      mediaSourceKey: Source;
      mediaStatusKey: Status;
      mediaTypeKey: Type;
      sourceThumbnailId?: string;
    }

    export type UpdateRequest = CreateRequest & {
      mediaId: string;
    };

    export type GetMediaRequest = Common.Api.PageRequest & {
      mediaStatusKey?: Status;
      mediaTypeKey?: Type;
      mediaSourceKey?: Source;
      sourceId?: string;
      mediaFolderId?: string;
    };

    export interface Item {
      mediaId: string;
      name: string;
      sourceId: string;
      sourceThumbnailId?: string;
      mediaSourceKey: Source;
      mediaStatusKey: Status;
      mediaTypeKey: Type;
      fileSize?: number;
    }

    export interface Entity {
      articleId: string | null;
      programId: string | null;
      programModuleId: string | null;
      programModuleLessonId: string | null;
      mediaId: string;
      userId: number | null;
      title: string;
    }
  }
}

export namespace JQuery {
  export interface CustomPosition {
    left: number | "auto";
    right: number | "auto";
    top: number | "auto";
    bottom: number | "auto";
  }

  export interface ToastOptions {
    text: string;
    heading?: string;
    showHideTransition?: "fade" | "slide" | "plain";
    allowToastClose?: boolean;
    hideAfter?: number | false;
    loader?: boolean;
    loaderBg?: string;
    stack?: number | false;
    position?:
      | "bottom-left"
      | "bottom-right"
      | "bottom-center"
      | "top-right"
      | "top-left"
      | "top-center"
      | "mid-center"
      | CustomPosition;
    bgColor?: string;
    textColor?: string;
    textAlign?: "left" | "right" | "center";
    icon?: "info" | "warning" | "error" | "success";
    beforeShow?: () => any;
    afterShown?: () => any;
    beforeHide?: () => any;
    afterHidden?: () => any;
  }
}

export type Booleanish = boolean | "true" | "false";

export type PageType =
  | "Account"
  | "Bridge"
  | "Checkout"
  | "Custom"
  | "Launch"
  | "OptIn"
  | "Sales"
  | "System"
  | "ThankYou"
  | "Trial"
  | "Upsell"
  | "Web";

export type PageSubType = "Checkout" | "Enroll" | "Regular" | "Login" | "Register" | "Survey" | "Trial";

export type UnitStandardKey = "Imperial" | "Metric";
